import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { Group } from '@app/tool/model/group.model';

import { ToolService } from '@services/tool.service';
import { AdminToolsTableComponent } from '@app/roles/admin/pages/tools/tools-table/tools-table.component';
import { ToolCreateModalComponent } from "@app/tool/tool-form-modal/tool-create-modal.component";
import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";
import * as FileSaver from "file-saver";
import { HttpResponse } from "@angular/common/http";
import { Tool } from "@app/tool/model/tool.model";
import { ToolFormModel } from "@app/tool/tool-form-modal/tool-create.model";
import { AddToolInSetModalComponent } from "@app/tool/components/add-tool-in-set-modal/add-tool-in-set-modal.component";
import { ConfirmDeleteModalComponent } from "@app/tool/components/confirm-delete-modal/confirm-delete-modal.component";
import { TranslateService } from "@ngx-translate/core";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { Subscription } from 'rxjs';
import { ObservableService } from '@app/core/services/observable.service';
import { ToolSet } from "@app/tool/model/toolSet.model";
import {
    ToolBulkUpdateModalComponent,
    ToolBulkResetModalComponent,
    ToolDocumentGenerationModalComponent
} from "@app/tool/components";
import { ToolBulkReset } from "@app/tool/components/tool-bulk-reset-modal/models/tool-bulk-reset.model";
import { ToolBulkResetRequest } from "@app/shared/models/tool-bulk-reset-request.model";
import { ToolBulkUpdate } from "@app/tool/components/tool-batch-update-modal/models/tool-bulk-update.model";
import { cloneDeep } from "lodash";
import { MenuItem } from 'primeng/api';

@Component({
    selector: 'app-tools-list',
    templateUrl: 'tools.component.html',
    styleUrls: ['tools.component.scss']
})
export class ToolListComponent implements OnInit, OnDestroy {
    _logger = Log.create(ToolListComponent.name);
    @ViewChild(AdminToolsTableComponent, {static: false}) adminToolsTable: AdminToolsTableComponent;
    @ViewChild(ToolCreateModalComponent, {static: false}) toolFormModal: ToolCreateModalComponent;
    @ViewChild(ToolBulkUpdateModalComponent, {static: false}) toolBulkUpdateModal: ToolBulkUpdateModalComponent;
    @ViewChild(ToolBulkResetModalComponent, { static: false }) toolBulkResetModal: ToolBulkResetModalComponent;
    @ViewChild(ToolDocumentGenerationModalComponent, { static: false }) toolDocumentGenerationModal: ToolDocumentGenerationModalComponent;
    @ViewChild(AddToolInSetModalComponent, {static: false}) addToolInSetFormModal: AddToolInSetModalComponent;
    @ViewChild(ConfirmDeleteModalComponent, {static: false}) deleteToolConfirmationModal: ConfirmDeleteModalComponent;

    isLoadingTools = true;
    isLoadingBulkUpdate = false;
    isLoadingBulkReset = false;
    isLoadingDocumentGeneration = false;

    toolsGrouped: Group[] = [];
    tableConfig = new TableConfig();

    scannedTools = [];
    toolSet;
    selectedTool = new ToolFormModel();

    filterBySecLines = false;
    filterBySecLinesId = undefined;
    toolServiceSubscription: Subscription;
    observableServiceSubscription: Subscription;
    filtersToggled = true;
    selectedTools: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();
    selectedToolCount = 0;
    selectableTools = true;
    allToolsAreSelected = false;

    batchActions: MenuItem[] = [];

    constructor(private toolService: ToolService,
                private permissionService: PermissionService,
                private cdr: ChangeDetectorRef,
                private router: Router,
                private translate: TranslateService,
                private toastr: ToastsManager,
                observableService: ObservableService) {
        this.observableServiceSubscription = observableService.hasChanged$.subscribe(
            () => {
                this.getTools(this.tableConfig);
            }
        )
    }

    ngOnInit() {
        this.buildBatchActions();
        this.getPaginationDetails();
        this.toolServiceSubscription = this.toolService.onReloadTools().subscribe(() => {
            this.getTools(this.tableConfig);
        });
    }

    ngOnDestroy() {
        this.toolServiceSubscription.unsubscribe();
        this.observableServiceSubscription.unsubscribe();
        sessionStorage.setItem('/tool-management.table.paginationDetails', JSON.stringify(this.tableConfig.pager));
    }

    addToolFromModal() {
        this.toolFormModal.open();
    }

    onDuplicateRow(tool) {
        this._logger.info("onDuplicateRow::", tool);
        this.toolFormModal.open(true);
        this.getToolDetails(tool);
    }

    onAddToolInSet(toolSet) {
        this._logger.info("onAddToolInSet::", toolSet);
        this.addToolInSetFormModal.open(toolSet);
        this.getToolDetails(toolSet.children[0]);
    }

    onDeleteSet(toolSet) {
        this._logger.info("onDeleteSet::", toolSet);
        this.toolSet = toolSet;
        this.scannedTools = toolSet.children;
        this.deleteToolConfirmationModal.open();
    }

    onCreateTool(tool) {
        this._logger.info("onCreateTool::", tool);
    }

    onUpdateTool(tool) {
        this._logger.info("onUpdateTool::", tool);
    }

    onDeleteTool(tool) {
        this._logger.info("onDeleteTool::", tool);
    }

    onPageChange(tableState) {
        this._logger.info("onPageChange::", tableState);
        const storedPaginationDetails = JSON.parse(sessionStorage.getItem('/tool-management.table.paginationDetails'));
        if (storedPaginationDetails) {
            return;
        }
        this.getTools(tableState);
    }

    onSortChange(tableState) {
        this._logger.info("onSortChange::", tableState);
        this.getTools(tableState);
    }

    onFilterChange(tableState) {
        this._logger.info("onFilterChange::", tableState);
        this.getTools(tableState);

        this.unselectAll();
    }

    onFilterGroupChange(tableState) {
        this.filterBySecLinesId = undefined;
        const lineFilter = this.tableConfig.filters.find(x => x.field === 'lineId');
        if (lineFilter) {
            this.filterBySecLinesId = lineFilter.value;
        }
        this.getTools(tableState);

        this.unselectAll();
    }

    onModalClose(event) {
        this.getTools(this.tableConfig);
    }

    onConfirmDeleteToolSet(deleteConfirmation) {
        this.toolService.deleteTool(deleteConfirmation).subscribe(
            (data) => {
                this._logger.info('deleteToolSet:: Success ', data);
                this.toolService.reloadTools();
                this.toastr.info(this.buildDeleteToolSuccessMessage());
            },
            (error) => {
                this._logger.error('deleteToolSet:: Error ', error);
                this.router.navigate(['/tool-management']);
            }
        )
    }

    // Get paginationDetails from Session Storage
    getPaginationDetails() {
        const storedPaginationDetails = JSON.parse(sessionStorage.getItem('/tool-management.table.paginationDetails'));
        if (storedPaginationDetails) {
            this.tableConfig.pager = storedPaginationDetails;
            setTimeout(() => {
                this.adminToolsTable.table.footer.paginator.updatePage(storedPaginationDetails.page);
            });
        }
    }

    private getTools(tableConfig = this.tableConfig) {
        this._logger.info("getTools::", this.tableConfig);
        this.isLoadingTools = true;
        this.toolService.getTools(tableConfig, false, this.filterBySecLines).subscribe(
            (data: any) => {
                this._logger.info("getTools:: API returned data", data);
                this.isLoadingTools = false;
                this.toolsGrouped = data.tools;
                this.tableConfig.pager.groupToolCount = data.count;
                this.tableConfig.pager.count = data.totalCount;
                const storedPaginationDetails = JSON.parse(sessionStorage.getItem('/tool-management.table.paginationDetails'));
                if (storedPaginationDetails) {
                    sessionStorage.removeItem('/tool-management.table.paginationDetails');
                }
            }, (error: any) => {
                this._logger.error("getTools:: API returned error", error);
                this.isLoadingTools = false;
            });
    }


    private getToolDetails(tool: Tool) {
        this._logger.info('getToolDetails:: Retrieving tool details', tool);
        this.toolService.getDetailsForTool(tool.id).subscribe(
            (data: any) => {
                this._logger.info('getToolDetails:: API returned data', data);
                tool.details = data;
                this.selectedTool = ToolFormModel.toolCopyOr(tool);
                this.cdr.detectChanges();
            }, (error: any) => {
                this._logger.error('getToolDetails:: API returned error', error);
            });
    }

    openToolDetails(tool) {
        this.router.navigate([`tool-management/${tool.id}`]);
    }

    openToolDetailsNewPage(tool) {
        const link = `tool-management/${tool.id}`;
        localStorage.setItem('openedInNewTab', 'true');
        window.open(link);
    }

    canSelectTools() {
        return this.canManageTools() || this.canResetTools() || this.canGenerateToolDocuments();
    }

    onFilterBySecLines(event: boolean) {
        this.filterBySecLinesId = undefined;
        const lineFilter = this.tableConfig.filters.find(x => x.field === 'lineId');
        this.filterBySecLines = event;
        if (lineFilter) {
            this.filterBySecLinesId = lineFilter.value;
        }
        this.getTools(this.tableConfig);
    }

    onFilterByDocumentationValidity(event: boolean) {
        const hasIncompleteDocumentationFilter = this.tableConfig.filters.find(x => x.field === 'hasIncompleteDocumentation');

        if (hasIncompleteDocumentationFilter) {
            hasIncompleteDocumentationFilter.value = event;
        } else {
            this.tableConfig.filters.push({field: 'hasIncompleteDocumentation', value: event});
        }

        this.getTools(this.tableConfig);
    }

    onDownloadToolsReport(tableConfig = this.tableConfig) {
        this.toolService.downloadToolReport(tableConfig).subscribe(
            (data: HttpResponse<any>) => {
                if (data.body != null) {
                    this._logger.info('onDownloadToolReport:: API Returned success', data);
                    const blob = new Blob([data.body], {type: 'application/xsls'});
                    const filename = data.headers.get('x-filename');
                    FileSaver.saveAs(blob, filename);
                }
            },
            (error: any) => {
                this._logger.error('onDownloadToolReport:: API Returned error', error);
            }
        );
    }

    onOpenBatchUpdate() {
        this.isLoadingBulkUpdate = false;
        this.toolBulkUpdateModal.open(this.selectedTools);
    }

    onOpenBatchReset() {
        this.isLoadingBulkReset = false;
        this.toolBulkResetModal.open(this.selectedTools);
    }

    onOpenDocumentGeneration() {
        this.isLoadingDocumentGeneration = false;
        this.toolDocumentGenerationModal.open(this.selectedTools);
    }

    onToolClicked(toolDetails: { checked: boolean, group: Tool | ToolSet }) {
        if (toolDetails.checked) {
            this.selectedTools.push(toolDetails.group);
        } else {
            const index = this.selectedTools.findIndex(item => item.id === toolDetails.group.id)
            if (index > -1) {
                this.selectedTools.splice(index, 1);
            }
            this.allToolsAreSelected = false;
        }
        this.selectedToolCount = this.computeSelectedToolLength(this.selectedTools);
        this.buildBatchActions();
        this.cdr.detectChanges();
        this._logger.info("onToolClicked:: Selected tools", this.selectedTools);
    }

    selectAll() {
        this.isLoadingTools = true;
        const selectAllTableConfig = cloneDeep(this.tableConfig);
        selectAllTableConfig.pager.pageSize = -1;
        this.buildBatchActions();

        this.toolService.getTools(selectAllTableConfig, false, this.filterBySecLines).subscribe(
            (data: any) => {
                this.selectedTools = data.tools;
                this.selectedToolCount = this.computeSelectedToolLength(this.selectedTools);
                this.allToolsAreSelected = true;
                this.cdr.detectChanges();

                this.isLoadingTools = false;
            }, (error: any) => {
                this._logger.error("getTools:: API returned error", error);
                this.isLoadingTools = false;
            });
    }

    unselectAll  () {
        this.selectedTools = [];
        this.selectedToolCount = this.computeSelectedToolLength(this.selectedTools);
        this.allToolsAreSelected = false;
        this.cdr.detectChanges();
    }

    toggleFilters() {
        this.filtersToggled = !this.filtersToggled;
    }

    computeSelectedToolLength(tools: Array<ToolSet | Tool>): number {
        return this.selectedTools.reduce((count, item) => {
            if (item instanceof ToolSet) {
                // If item is a ToolSet, count all its tools
                return count + item?.tools.length;
            } else {
                // If item is an individual Tool, count it as 1
                return count + 1;
            }
        }, 0);
    }

    private buildDeleteToolSuccessMessage() {
        return this.translate.instant('admin.tools.toasts.deleted',
            {toolName: this.toolSet.name});
    }

    private buildBatchActions() {
        this.batchActions = [];

        if (this.canManageTools()) {
            this.batchActions.push({
                label: this.translate.instant('admin.tools.batchUpdate.button'),
                icon: 'pi pi-refresh',
                command: () => this.onOpenBatchUpdate()
            });
        }

        if (this.canResetTools()) {
            this.batchActions.push({
                label: this.translate.instant('admin.tools.bulkReset.button'),
                icon: 'pi pi-undo',
                command: () => this.onOpenBatchReset()
            });
        }

        if (this.canGenerateToolDocuments()) {
            this.batchActions.push({
                label: this.translate.instant('admin.tools.generateDocuments.button'),
                icon: 'pi pi-file',
                command: () => this.onOpenDocumentGeneration()
            });
        }
    }

    onBulkUpdate(props: ToolBulkUpdate) {
        this.isLoadingBulkUpdate = true;
        this.toolService.updateToolsInBulk(props).subscribe(
            (data: any) => {
                this.getTools();
                this._logger.info("onBatchUpdate:: API returned data", data);
                this.isLoadingBulkUpdate = false;
                this.toolBulkUpdateModal.close();
                this.toastr.success(this.translate.instant('admin.tools.batchUpdate.notifications.success'), this.translate.instant('generic.success'));

                this.unselectAll();
            }, (error: any) => {
                this._logger.error("onBatchUpdate:: API returned error", error);
                this.isLoadingBulkUpdate = false;
                this.toastr.error(this.translate.instant('admin.tools.batchUpdate.notifications.failed'), this.translate.instant('generic.error'));
            });
    }

    onBulkReset(reset: ToolBulkReset) {
        this.isLoadingBulkReset = true;

        const toolIds: number[] = [];
        reset.tools.forEach(t => {
        if (t instanceof ToolSet) {
            toolIds.push(...t.tools.map(child => child.id));
        } else {
            toolIds.push(t.id);
        }
        });

        const dto: ToolBulkResetRequest = { toolIds };

        this.toolService.resetToolsBulk(dto).subscribe(
            (data: any) => {
            this._logger.info("onBulkReset:: API returned data", data);

            this.getTools();
            this.isLoadingBulkReset = false;
            this.toolBulkResetModal.close();

            this.toastr.success(
                this.translate.instant('admin.tools.bulkReset.notifications.success'),
                this.translate.instant('generic.success')
            );

            this.unselectAll();
            },
            (error: any) => {
            this._logger.error("onBulkReset:: API returned error", error);
            this.isLoadingBulkReset = false;

            this.toastr.error(
                this.translate.instant('admin.tools.bulkReset.notifications.failed'),
                this.translate.instant('generic.error')
            );
            }
        );
    }

    onGenerateDocuments(toolIds: number[]) {
        this.isLoadingDocumentGeneration = true;

        this.toolService.generateToolAcceptanceReport(toolIds).subscribe(
            (data: HttpResponse<any>) => {
                this._logger.info('onGenerateDocuments:: API returned data', data);
                const filename = data.headers.get('x-filename') || 'tool-acceptance-report';

                if (data.body != null) {
                    FileSaver.saveAs(data.body, filename);
                }

                this.isLoadingDocumentGeneration = false;
                this.toolDocumentGenerationModal.close();
                this.unselectAll();
            },
            (error: any) => {
                this._logger.error('onGenerateDocuments:: API returned error', error);
                this.isLoadingDocumentGeneration = false;
                this.toastr.error(
                    this.translate.instant('admin.tools.generateDocuments.notifications.failed'),
                    this.translate.instant('generic.error')
                );
            }
        );
    }

    canManageTools() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_TOOLS');
    }

    canResetTools() {
        return this.permissionService.hasHasPermissionForFeature('RESET_TOOLS');
    }

    canGenerateToolDocuments() {
        return this.permissionService.hasHasPermissionForFeature('GENERATE_TOOL_DOCUMENTS');
    }
}
